'use client';

import { useEffect } from 'react';

/**
 * Registers /sw.js so the app is installable (Add to Home Screen → standalone)
 * and resilient offline. Renders nothing. Registration is deferred to `load`
 * so it never competes with the first paint. No-op where service workers are
 * unavailable (older browsers, insecure origins).
 *
 * IMPORTANT: the SW caches build assets (/_next/static, *.js) cache-first. That
 * is correct for production (hashed, immutable filenames) but breaks the dev
 * server, where Turbopack reuses chunk paths with new contents — the SW then
 * serves a stale chunk and the app throws "module factory is not available".
 * So we ONLY register in production, and in development we actively unregister
 * any previously-installed SW and purge its caches to self-heal affected dev
 * machines.
 */
export default function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    if (process.env.NODE_ENV !== 'production') {
      // Dev: tear down any SW left over from a prior prod build / earlier run,
      // then drop its caches so stale chunks can't be served again.
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => { /* non-fatal */ });
      if ('caches' in window) {
        caches.keys()
          .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
          .catch(() => { /* non-fatal */ });
      }
      return;
    }

    const register = () => {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* non-fatal */ });
    };
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
  }, []);

  return null;
}
